import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { FolderOpen, AlertCircle, Copy, CheckCircle } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import api from "@/lib/api";

interface PatientGoogleDriveImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onImportComplete: () => void;
}

const SERVICE_ACCOUNT_EMAIL = "april3rd2025@pear-voice-prod.iam.gserviceaccount.com";

const PatientGoogleDriveImportModal = ({
  isOpen,
  onClose,
  organizationId,
  onImportComplete,
}: PatientGoogleDriveImportModalProps) => {
  const [folderId, setFolderId] = useState("");
  const [folderUrl, setFolderUrl] = useState("");
  const [recursive, setRecursive] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const { toast } = useToast();

  const extractFolderId = (url: string): string => {
    // Extract folder ID from Google Drive URL
    // Format: https://drive.google.com/drive/folders/{FOLDER_ID}
    const match = url.match(/\/folders\/([a-zA-Z0-9-_]+)/);
    return match ? match[1] : "";
  };

  const handleFolderUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    setFolderUrl(url);
    const extractedId = extractFolderId(url);
    setFolderId(extractedId);
  };

  const handleFolderIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFolderId(e.target.value);
    setFolderUrl(""); // Clear URL if user directly enters folder ID
  };

  const copyServiceAccountEmail = async () => {
    try {
      await navigator.clipboard.writeText(SERVICE_ACCOUNT_EMAIL);
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
      toast({
        title: "Copied!",
        description: "Service account email copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Failed to copy",
        description: "Please copy the email manually",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!folderId.trim()) {
      toast({
        title: "Missing folder ID",
        description: "Please enter a Google Drive folder ID or URL",
        variant: "destructive",
      });
      return;
    }

    setIsImporting(true);
    try {
      const response = await api.ingestPatientFilesFromGoogleDrive(
        organizationId,
        folderId.trim(),
        recursive
      );

      toast({
        title: "Import successful",
        description: "Patient files have been imported from Google Drive",
      });

      setFolderId("");
      setFolderUrl("");
      setRecursive(false);
      onImportComplete();
      onClose();
    } catch (error: any) {
      console.error("Import failed:", error);
      const errorMessage = error.response?.data?.detail || 
        "Failed to import files from Google Drive. Please ensure the folder is shared with the service account.";
      
      toast({
        title: "Import failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      setFolderId("");
      setFolderUrl("");
      setRecursive(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Patient Charts from Google Drive</DialogTitle>
          <DialogDescription>
            Import patient chart files (HTML, PDF, JSON, Google Docs) from a Google Drive folder
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Important Notice */}
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="space-y-3">
              <p className="font-semibold">Before importing, you must share your Google Drive folder:</p>
              <div className="space-y-2">
                <p>1. Open your Google Drive folder</p>
                <p>2. Click the "Share" button</p>
                <p>3. Add this email and grant "Viewer" access:</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="bg-muted px-2 py-1 rounded text-xs flex-1 overflow-x-auto">
                    {SERVICE_ACCOUNT_EMAIL}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={copyServiceAccountEmail}
                    className="shrink-0"
                  >
                    {copiedEmail ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-1" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </AlertDescription>
          </Alert>

          {/* Folder URL Input */}
          <div className="space-y-2">
            <Label htmlFor="folder-url">Google Drive Folder URL</Label>
            <Input
              id="folder-url"
              type="url"
              placeholder="https://drive.google.com/drive/folders/..."
              value={folderUrl}
              onChange={handleFolderUrlChange}
              disabled={isImporting}
            />
            <p className="text-sm text-muted-foreground">
              Paste the full URL of your Google Drive folder
            </p>
          </div>

          {/* OR Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">OR</span>
            </div>
          </div>

          {/* Folder ID Input */}
          <div className="space-y-2">
            <Label htmlFor="folder-id">Folder ID</Label>
            <div className="flex items-center space-x-2">
              <FolderOpen className="h-5 w-5 text-muted-foreground/60" />
              <Input
                id="folder-id"
                type="text"
                placeholder="1A2B3C4D5E6F7G8H9I0J"
                value={folderId}
                onChange={handleFolderIdChange}
                disabled={isImporting}
                className="flex-1"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Enter just the folder ID from the URL
            </p>
          </div>

          {/* Recursive Option */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="recursive"
              checked={recursive}
              onCheckedChange={(checked) => setRecursive(checked as boolean)}
              disabled={isImporting}
            />
            <Label
              htmlFor="recursive"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              Include files from subfolders (recursive)
            </Label>
          </div>

          {/* File Types Info */}
          <div className="bg-muted/50 p-4 rounded-lg">
            <p className="text-sm text-foreground/80 font-medium mb-2">
              Supported file types:
            </p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• HTML files (.html)</li>
              <li>• PDF documents (.pdf)</li>
              <li>• JSON files (.json)</li>
              <li>• Google Docs (automatically converted to HTML)</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isImporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleImport}
            disabled={isImporting || !folderId.trim()}
          >
            {isImporting ? "Importing..." : "Import Files"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PatientGoogleDriveImportModal;